import {
  classifyUrl,
  isDriveKind,
  searchHive,
  storagePathFor,
  type HiveFile,
  type ItemType,
  type Json,
  type MatchedBy,
  type SearchMode,
  type VaultLink,
  type VaultLinkKind,
} from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DriveError, getFile, searchDrive, type DriveClient, type DriveFile } from '@/lib/drive';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

// Vault's data: saved links, files in Hive storage, links between items, and
// the member's Google Drive connection (the token itself stays in the `google`
// Edge Function; the app only ever sees short-lived access tokens).

export type VaultLinkRow = Omit<VaultLink, 'search'>;
export type HiveFileRow = Omit<HiveFile, 'search'>;

const LINK_COLUMNS =
  'id, url, title, kind, provider_id, thumbnail_url, preview, notes, game_id, source, source_id, created_at, updated_at, created_by';
const FILE_COLUMNS =
  'id, name, mime_type, size_bytes, storage_path, width, height, game_id, created_at, updated_at, created_by';

export const HIVE_FILES_BUCKET = 'hive-files';

export function useVaultLinks() {
  return useQuery({
    queryKey: queryKeys.vaultLinks,
    queryFn: async (): Promise<VaultLinkRow[]> => {
      const { data, error } = await supabase()
        .from('vault_links')
        .select(LINK_COLUMNS)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as VaultLinkRow[];
    },
  });
}

export function useFiles() {
  return useQuery({
    queryKey: queryKeys.files,
    queryFn: async (): Promise<HiveFileRow[]> => {
      const { data, error } = await supabase()
        .from('files')
        .select(FILE_COLUMNS)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as HiveFileRow[];
    },
  });
}

/** What the link-preview function says about a web or Figma URL. */
export interface LinkPreview {
  title: string | null;
  description: string | null;
  image: string | null;
  site: string | null;
}

/** Asks the link-preview Edge Function about a URL. */
export async function previewLink(url: string): Promise<LinkPreview> {
  const { data, error } = await supabase().functions.invoke<LinkPreview>('link-preview', { body: { url } });
  if (error) throw new Error(await functionErrorMessage(error, 'Couldn’t preview that link.'));
  return data!;
}

/**
 * The title and preview for a pasted link: Drive asks Drive itself (which only
 * works for files Hive can see; otherwise the title stays empty and can be
 * typed in), everything else goes through link-preview. Never throws.
 */
export async function describeLink(url: string): Promise<Pick<NewVaultLink, 'title' | 'thumbnail_url' | 'preview'>> {
  const classified = classifyUrl(url);
  if (!classified) return {};
  try {
    if (isDriveKind(classified.kind) && classified.providerId) {
      const file = await getFile(driveClient, classified.providerId);
      return { title: file.name, preview: { mime_type: file.mimeType } };
    }
    const p = await previewLink(classified.url);
    return {
      title: p.title ?? '',
      thumbnail_url: p.image,
      preview: { description: p.description, site: p.site, image: p.image },
    };
  } catch {
    return {};
  }
}

/** The message an Edge Function put in its JSON error body, if any. */
async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as { error?: string };
      if (body.error) return body.error;
    } catch {
      // Not JSON.
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

export interface NewVaultLink {
  url: string;
  title?: string;
  kind?: VaultLinkKind;
  provider_id?: string | null;
  thumbnail_url?: string | null;
  preview?: Json;
  notes?: string;
  game_id?: string | null;
}

/**
 * Saves a link, or returns the one already saved for the same URL (URLs are
 * unique, so pasting a link twice reuses the row).
 */
export async function saveVaultLink(input: NewVaultLink): Promise<VaultLinkRow> {
  const classified = classifyUrl(input.url);
  if (!classified) throw new Error('That isn’t a web link (http or https).');
  const client = supabase();
  const existing = await client.from('vault_links').select(LINK_COLUMNS).eq('url', classified.url).maybeSingle();
  if (existing.error) throw existing.error;
  if (existing.data) return existing.data as VaultLinkRow;
  const { data, error } = await client
    .from('vault_links')
    .insert({
      url: classified.url,
      kind: input.kind ?? classified.kind,
      provider_id: input.provider_id ?? classified.providerId,
      title: input.title ?? '',
      thumbnail_url: input.thumbnail_url ?? null,
      preview: input.preview ?? {},
      notes: input.notes ?? '',
      game_id: input.game_id ?? null,
    })
    .select(LINK_COLUMNS)
    .single();
  if (error) throw error;
  return data as VaultLinkRow;
}

/** Paste-a-link: previews it (best effort), then saves it. */
export function useAddVaultLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { url: string; game_id?: string | null; notes?: string }) => {
      // Saving works without a preview too: an untitled link shows its URL.
      return saveVaultLink({
        url: input.url,
        game_id: input.game_id ?? null,
        notes: input.notes ?? '',
        ...(await describeLink(input.url)),
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.vaultLinks }),
  });
}

export function useUpdateVaultLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<VaultLinkRow, 'title' | 'notes' | 'game_id'>>;
    }) => {
      const { error } = await supabase().from('vault_links').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.vaultLinks }),
  });
}

export function useDeleteVaultItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (item: { type: 'vault_link'; id: string } | { type: 'file'; file: HiveFileRow }) => {
      const client = supabase();
      if (item.type === 'vault_link') {
        const { error } = await client.from('vault_links').delete().eq('id', item.id);
        if (error) throw error;
        return;
      }
      const removed = await client.storage.from(HIVE_FILES_BUCKET).remove([item.file.storage_path]);
      if (removed.error) throw removed.error;
      const { error } = await client.from('files').delete().eq('id', item.file.id);
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.vaultLinks }),
        queryClient.invalidateQueries({ queryKey: queryKeys.files }),
        queryClient.invalidateQueries({ queryKey: queryKeys.links }),
      ]),
  });
}

/** Uploads a file to Hive storage and records it. */
export async function uploadToHive(
  file: File,
  options: { gameId: string | null; gameSlug: string | null; width?: number; height?: number },
) {
  const client = supabase();
  const path = storagePathFor(options.gameSlug, file.name, crypto.randomUUID().slice(0, 8));
  const up = await client.storage
    .from(HIVE_FILES_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
  if (up.error) throw up.error;
  const { data, error } = await client
    .from('files')
    .insert({
      name: file.name,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      storage_path: path,
      width: options.width ?? null,
      height: options.height ?? null,
      game_id: options.gameId,
    })
    .select(FILE_COLUMNS)
    .single();
  if (error) {
    await client.storage.from(HIVE_FILES_BUCKET).remove([path]);
    throw error;
  }
  return data as HiveFileRow;
}

/**
 * The Drive API's thumbnail for a Drive file, when this member has Drive
 * connected and Hive can see the file. Unlike Drive's own thumbnail URL it
 * doesn't need a Google sign-in in the window, so it works in the desktop app.
 */
export function useDriveThumbnail(link: Pick<VaultLinkRow, 'kind' | 'provider_id'>) {
  const account = useMyGoogleAccount();
  const fileId = isDriveKind(link.kind) && link.kind !== 'drive_folder' ? link.provider_id : null;
  return useQuery({
    queryKey: ['drive_thumbnail', fileId],
    enabled: !!fileId && account.data?.connected === true,
    staleTime: 45 * 60_000,
    retry: false,
    queryFn: async () => (await getFile(driveClient, fileId!)).thumbnailLink ?? null,
  });
}

/** A short-lived URL to show or download a Hive file. */
export function useSignedFileUrl(storagePath: string | null) {
  return useQuery({
    queryKey: ['files', 'signed', storagePath],
    enabled: !!storagePath,
    staleTime: 50 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase().storage.from(HIVE_FILES_BUCKET).createSignedUrl(storagePath!, 3600);
      if (error) throw error;
      return data.signedUrl;
    },
  });
}

// ---------------------------------------------------------------------------
// Links between items
// ---------------------------------------------------------------------------

export interface ItemRef {
  type: ItemType;
  id: string;
}

export interface LinkedItem {
  /** The links row. */
  linkId: string;
  kind: string;
  type: ItemType;
  id: string;
  title: string;
  game_id: string | null;
  parent_id: string | null;
  /** Gone, or not visible: its row in `items` is missing. */
  missing: boolean;
}

export interface ItemLinks {
  to: LinkedItem[];
  from: LinkedItem[];
}

type ItemRow = { type: string; id: string; title: string; game_id: string | null; parent_id: string | null };

/** Looks items up in the `items` view, by type and id. */
export async function lookupItems(refs: readonly ItemRef[]): Promise<Map<string, ItemRow>> {
  const out = new Map<string, ItemRow>();
  const ids = [...new Set(refs.map((r) => r.id))];
  if (ids.length === 0) return out;
  const { data, error } = await supabase().from('items').select('type, id, title, game_id, parent_id').in('id', ids);
  if (error) throw error;
  for (const row of data as ItemRow[]) out.set(`${row.type}:${row.id}`, row);
  return out;
}

/** What an item links to and what links to it, with titles. */
export function useItemLinks(item: ItemRef) {
  return useQuery({
    queryKey: queryKeys.itemLinks(item.type, item.id),
    queryFn: async (): Promise<ItemLinks> => {
      const client = supabase();
      const [out, back] = await Promise.all([
        client.from('links').select('id, kind, to_type, to_id').eq('from_type', item.type).eq('from_id', item.id),
        client.from('links').select('id, kind, from_type, from_id').eq('to_type', item.type).eq('to_id', item.id),
      ]);
      if (out.error) throw out.error;
      if (back.error) throw back.error;
      const toRefs = out.data.map((l) => ({ linkId: l.id, kind: l.kind, type: l.to_type as ItemType, id: l.to_id }));
      const fromRefs = back.data.map((l) => ({
        linkId: l.id,
        kind: l.kind,
        type: l.from_type as ItemType,
        id: l.from_id,
      }));
      const found = await lookupItems([...toRefs, ...fromRefs]);
      const fill = (r: (typeof toRefs)[number]): LinkedItem => {
        const row = found.get(`${r.type}:${r.id}`);
        return {
          ...r,
          title: row?.title ?? 'Missing item',
          game_id: row?.game_id ?? null,
          parent_id: row?.parent_id ?? null,
          missing: !row,
        };
      };
      return { to: toRefs.map(fill), from: fromRefs.map(fill) };
    },
  });
}

export function useLinkItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ from, to, kind = 'relates' }: { from: ItemRef; to: ItemRef; kind?: string }) => {
      const { error } = await supabase()
        .from('links')
        .insert({ from_type: from.type, from_id: from.id, to_type: to.type, to_id: to.id, kind });
      // Already linked this way: nothing to do.
      if (error && error.code !== '23505') throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.links }),
  });
}

export function useUnlink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await supabase().from('links').delete().eq('id', linkId);
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.links }),
  });
}

export type SearchHit = {
  item_type: ItemType;
  item_id: string;
  game_id: string | null;
  title: string;
  snippet: string;
  status: string | null;
  parent_id: string | null;
  updated_at: string;
  /** Scan only: matched by its words, its meaning, or both. */
  matched?: MatchedBy;
};

/** One search across Hive (search_items), for Scan and the Link… picker. */
export function useSearch(
  query: string,
  options: { types?: ItemType[] | null; gameId?: string | null; limit?: number } = {},
) {
  const q = query.trim();
  return useQuery({
    queryKey: ['search', q, options.types ?? null, options.gameId ?? null, options.limit ?? 30],
    enabled: q.length > 0,
    staleTime: 15_000,
    queryFn: async (): Promise<SearchHit[]> => {
      const { data, error } = await supabase().rpc('search_items', {
        query: q,
        max_results: options.limit ?? 30,
        ...(options.types?.length ? { item_types: options.types } : {}),
        ...(options.gameId ? { game_id: options.gameId } : {}),
      });
      if (error) throw error;
      return data as SearchHit[];
    },
  });
}

/** A Drive file matching a search, with the Vault link that holds it when Hive has one. */
export interface DriveHit {
  file: DriveFile;
  link: { id: string; title: string; game_id: string | null } | null;
}

/** Scan's "In Drive files": Drive's full-text search with the member's own token. */
export function useDriveSearch(query: string, enabled: boolean, client: DriveClient = driveClient) {
  const q = query.trim();
  return useQuery({
    queryKey: ['drive-search', q],
    enabled: enabled && q.length > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<DriveHit[]> => {
      const files = await searchDrive(client, q);
      if (!files.length) return [];
      const { data, error } = await supabase()
        .from('vault_links')
        .select('id, title, game_id, provider_id')
        .in(
          'provider_id',
          files.map((f) => f.id),
        );
      if (error) throw error;
      const byFile = new Map(data.map((l) => [l.provider_id, l]));
      return files.map((file) => {
        const l = byFile.get(file.id);
        return { file, link: l ? { id: l.id, title: l.title, game_id: l.game_id } : null };
      });
    },
  });
}

/** Scan's search: keywords and meaning blended (or keywords only), with why meaning search is off, if it is. */
export function useScanSearch(
  query: string,
  options: { types?: ItemType[] | null; gameId?: string | null; limit?: number; mode?: SearchMode } = {},
) {
  const q = query.trim();
  const mode = options.mode ?? 'both';
  return useQuery({
    queryKey: ['scan-search', q, mode, options.types ?? null, options.gameId ?? null, options.limit ?? 60],
    enabled: q.length > 0,
    staleTime: 15_000,
    queryFn: async (): Promise<{ hits: SearchHit[]; meaningUnavailable?: string }> => {
      const out = await searchHive(supabase(), {
        query: q,
        mode,
        types: options.types,
        gameId: options.gameId,
        limit: options.limit ?? 60,
      });
      return { hits: out.hits as SearchHit[], meaningUnavailable: out.meaningUnavailable };
    },
  });
}

// ---------------------------------------------------------------------------
// Google Drive connection
// ---------------------------------------------------------------------------

export interface GoogleAccount {
  connected: boolean;
  scopes: string | null;
  drive_root_folder_id: string | null;
  connected_at: string | null;
}

export function useMyGoogleAccount() {
  return useQuery({
    queryKey: queryKeys.googleAccount,
    queryFn: async (): Promise<GoogleAccount> => {
      const { data, error } = await supabase().rpc('my_google_account');
      if (error) throw error;
      const row = (data as GoogleAccount[] | null)?.[0];
      return row ?? { connected: false, scopes: null, drive_root_folder_id: null, connected_at: null };
    },
  });
}

export function useStudioSettings() {
  return useQuery({
    queryKey: queryKeys.studioSettings,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('studio_settings')
        .select('id, drive_uploads_folder_id')
        .maybeSingle();
      if (error) throw error;
      return data ?? { id: null, drive_uploads_folder_id: null };
    },
  });
}

/** Records the Hive Uploads folder: for the studio (if `forStudio`) and as this member's picked root. */
export function useSetUploadsFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ folderId, forStudio }: { folderId: string; forStudio: boolean }) => {
      const client = supabase();
      if (forStudio) {
        const settings = await client.from('studio_settings').select('id').single();
        if (settings.error) throw settings.error;
        const { error } = await client
          .from('studio_settings')
          .update({ drive_uploads_folder_id: folderId })
          .eq('id', settings.data.id);
        if (error) throw error;
      }
      const { error } = await client.rpc('set_drive_root_folder', { p_folder_id: folderId });
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.studioSettings }),
        queryClient.invalidateQueries({ queryKey: queryKeys.googleAccount }),
      ]),
  });
}

type GoogleResponse = { access_token?: string; expires_in?: number; connected?: boolean };

async function callGoogle(body: Record<string, unknown>): Promise<GoogleResponse> {
  const { data, error } = await supabase().functions.invoke<GoogleResponse>('google', { body });
  if (error) throw new DriveError(await functionErrorMessage(error, 'Google Drive isn’t reachable.'), 0);
  return data ?? {};
}

let cached: { token: string; expires: number } | null = null;

/** A Drive access token for the signed-in member, cached until a minute before it expires. */
export async function driveAccessToken(): Promise<string> {
  if (cached && cached.expires > Date.now() + 60_000) return cached.token;
  const res = await callGoogle({ action: 'token' });
  if (!res.access_token) throw new DriveError('Connect Google Drive first.', 409);
  cached = { token: res.access_token, expires: Date.now() + (res.expires_in ?? 3600) * 1000 };
  return cached.token;
}

export const driveClient: DriveClient = { token: driveAccessToken };

/** Hands the refresh token from the Drive consent sign-in to the `google` function. */
export async function connectDrive(refreshToken: string): Promise<void> {
  const res = await callGoogle({ action: 'connect', refresh_token: refreshToken });
  if (res.access_token) cached = { token: res.access_token, expires: Date.now() + (res.expires_in ?? 3600) * 1000 };
}

export function useDisconnectDrive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await callGoogle({ action: 'disconnect' });
      cached = null;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.googleAccount }),
  });
}
